import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BudgetRollup,
  CreateScenarioInput,
  Scenario,
  ScenarioProjection,
  VendorCategory,
} from '@guabi/shared';
import { api, apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/lib/api';

export const budgetQueryKey = (wedId: string) => ['budget', wedId] as const;
export const scenariosQueryKey = (wedId: string) => ['scenarios', wedId] as const;

export function useBudget(wedId: string) {
  return useQuery({
    queryKey: budgetQueryKey(wedId),
    queryFn: () => apiGet<BudgetRollup>(`/weddings/${wedId}/budget`),
  });
}

export function useScenarios(wedId: string) {
  return useQuery({
    queryKey: scenariosQueryKey(wedId),
    queryFn: () => apiGet<{ items: Scenario[] }>(`/weddings/${wedId}/scenarios`),
  });
}

/**
 * A projection is computed per request rather than stored, so it is its own
 * query keyed by the scenario — editing a supplier's quote should move it.
 */
export function useProjection(wedId: string, scenarioId: string | undefined) {
  return useQuery({
    queryKey: ['projection', wedId, scenarioId],
    queryFn: () =>
      apiGet<ScenarioProjection>(`/weddings/${wedId}/scenarios/${scenarioId ?? ''}/projection`),
    enabled: Boolean(scenarioId),
  });
}

/** Planned changes move the rollup and the Home tab; nothing else. */
export function useSetPlanned(wedId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { category: VendorCategory; plannedCents: number }>({
    mutationFn: ({ category, plannedCents }) =>
      apiPut(`/weddings/${wedId}/budget/${category}`, { plannedCents }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: budgetQueryKey(wedId) });
      void qc.invalidateQueries({ queryKey: ['summary', wedId] });
      // Every projection compares against planned, so they all move too.
      void qc.invalidateQueries({ queryKey: ['projection', wedId] });
    },
  });
}

function useScenarioMutation<TArgs, TResult = void>(
  fn: (args: TArgs) => Promise<TResult>,
  wedId: string,
) {
  const qc = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scenariosQueryKey(wedId) });
      void qc.invalidateQueries({ queryKey: ['projection', wedId] });
    },
  });
}

export function useCreateScenario(wedId: string) {
  return useScenarioMutation<CreateScenarioInput, Scenario>(
    (input) => apiPost<Scenario>(`/weddings/${wedId}/scenarios`, input),
    wedId,
  );
}

export function useUpdateScenario(wedId: string) {
  return useScenarioMutation<
    { scenarioId: string; input: Partial<CreateScenarioInput> & { updatedAt: string } },
    Scenario
  >(
    ({ scenarioId, input }) =>
      apiPatch<Scenario>(`/weddings/${wedId}/scenarios/${scenarioId}`, input),
    wedId,
  );
}

export function useDeleteScenario(wedId: string) {
  return useScenarioMutation<{ scenarioId: string }>(
    ({ scenarioId }) => apiDelete(`/weddings/${wedId}/scenarios/${scenarioId}`),
    wedId,
  );
}

/**
 * The budget in a few sentences, on request.
 *
 * Sends nothing but a locale: the server recomputes every figure, because a
 * narrative written on numbers the client supplied would read exactly as
 * convincingly as a true one.
 */
export function useBudgetNarrative(wedId: string) {
  const qc = useQueryClient();
  return useMutation<{ text: string }, Error, void>({
    mutationFn: () =>
      api<{ text: string }>(`/weddings/${wedId}/ai/budget-narrative`, {
        method: 'POST',
        body: JSON.stringify({ locale: navigator.language }),
        timeoutMs: 28_000,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ai-usage', wedId] }),
  });
}
